<script setup lang="ts">
import {
  ComboboxAnchor,
  ComboboxCancel,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
  Label,
} from 'reka-ui'
import { computed, nextTick, ref, watch } from 'vue'

import SourceHint from '~/components/ui/SourceHint.vue'
import { useOverlayPopperZ } from '~/composables/ui/useOverlayStack'

type Size = 'xsmall' | 'small' | 'medium'
type Theme = 'light' | 'dark'

type Props = {
  label: string
  theme?: Theme
  size?: Size
  disabled?: boolean
  options: string[]
  selectedOption?: string
  error?: string
  /** Texte d’aide sous le champ (ignoré si `error` est renseigné). */
  helper?: string
}

const props = withDefaults(defineProps<Props>(), {
  theme: 'light',
  size: 'small',
  disabled: false,
  selectedOption: '',
  error: '',
  helper: '',
})

const emit = defineEmits<{
  (e: 'update:selectedOption', value: string): void
}>()

const overlayPopperZ = useOverlayPopperZ()
const selectContainerRef = ref<HTMLElement | null>(null)
const dropdownWidth = ref<number | null>(null)
const isOpen = ref(false)

const labelId = computed(() => props.label.replace(/\s+/g, '_').toLowerCase())
const hintId = computed(() => `${labelId.value}-hint`)
const hintText = computed(() => (props.error?.trim() ? props.error.trim() : (props.helper ?? '').trim()))
const describedBy = computed(() => (hintText.value ? hintId.value : undefined))
const selected = computed({
  get: () => props.selectedOption,
  set: (v: string) => emit('update:selectedOption', v ?? '')
})

watch(isOpen, async (open) => {
  if (!open) {
    dropdownWidth.value = null
    return
  }
  await nextTick()
  const anchor = selectContainerRef.value?.querySelector('.source-select__anchor')
  if (anchor instanceof HTMLElement) dropdownWidth.value = anchor.offsetWidth
})

/** Empêche la modale (overflow: auto) de « voler » le défilement de la liste. */
function onSelectViewportWheel(wheelEvent: WheelEvent): void {
  wheelEvent.stopPropagation()
}
</script>

<template>
  <div ref="selectContainerRef" class="source-select" :class="[size, { 'is-open': isOpen }, { 'has-error': error }]">
    <ComboboxRoot v-model="selected" v-model:open="isOpen" :disabled="disabled">
      <Label :for="labelId" class="source-select__label">{{ label }}</Label>
      <ComboboxAnchor class="source-select__anchor">
        <ComboboxInput
          :id="labelId"
          :display-value="(v: string) => v ?? ''"
          placeholder="Sélectionner"
          class="source-select__input"
          :aria-invalid="!!error"
          :aria-describedby="describedBy"
        />
        <span class="source-select__actions">
          <ComboboxCancel
            v-if="selectedOption"
            class="source-select__cancel"
            aria-label="Effacer la sélection"
          >
            <span class="material-symbols-rounded">close</span>
          </ComboboxCancel>
          <ComboboxTrigger class="source-select__trigger" aria-label="Ouvrir la liste">
            <span class="material-symbols-rounded source-select__chevron">expand_more</span>
          </ComboboxTrigger>
        </span>
      </ComboboxAnchor>
      <ComboboxPortal :disabled="true">
        <ComboboxContent
          class="source-select__content"
          position="popper"
          side="bottom"
          :side-offset="4"
          :style="[dropdownWidth ? { width: `${dropdownWidth}px` } : {}, { zIndex: overlayPopperZ }]"
        >
          <ComboboxViewport class="source-select__viewport" @wheel="onSelectViewportWheel">
            <ComboboxItem
              v-for="option in options"
              :key="option"
              :value="option"
              class="source-select__item"
            >
              <ComboboxItemIndicator class="source-select__item-indicator" />
              {{ option }}
            </ComboboxItem>
          </ComboboxViewport>
        </ComboboxContent>
      </ComboboxPortal>
    </ComboboxRoot>
    <SourceHint
      v-if="hintText"
      :hint-id="hintId"
      :text="hintText"
      :state="error ? 'error' : 'default'"
    />
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/index" as *;
@use "~/assets/scss/partials/elpdl/spacing" as *;
@use "~/assets/scss/app" as *;

@mixin source-select-size($label-extend, $input-extend, $height, $line-height, $padding) {
  .source-select__label { @extend .#{$label-extend}; }
  .source-select__input { @extend .#{$input-extend}; padding: $padding; line-height: $line-height; }
  .source-select__anchor { height: $height; }
}

.source-select {
  display: flex;
  flex-direction: column;
  gap: $spacing-2xs;
  min-width: 224px;
  width: 100%;
  max-width: 100%;

  &__label {
    @extend .text-2xs;
    color: var(--color-text-secondary);
    cursor: default;
  }

  &__anchor {
    display: flex;
    align-items: center;
    height: 32px;
    min-width: 0;
    border: 1px solid var(--color-border-input);
    border-radius: 4px;
    background: var(--color-bg-input);
    box-sizing: border-box;
    outline: none;
    overflow: hidden;
    &:hover {
      border-color: var(--color-primary-hover);
      background-color: var(--color-bg-hover-primary);
    }
    &:focus-within,
    .source-select.is-open & { border-color: var(--color-primary-hover); }
    &[data-disabled] {
      background-color: var(--color-disabled);
      border: none;
      outline: none;
    }
  }

  &.has-error .source-select__anchor {
    border-color: var(--color-danger);
  }

  .source-select__anchor :deep(input),
  .source-select__anchor :deep(button) {
    outline: none !important;
    box-shadow: none !important;
  }

  &__input {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: none;
    background: transparent;
    padding: 0 $spacing-2xs 0 $spacing-xs;
    outline: none;
    line-height: 30px;
    border-radius: 3px 0 0 3px;
    &::placeholder { color: c.$enedis-neutral-100; }
  }

  &__actions {
    display: inline-flex;
    align-items: center;
    border-left: 1px solid var(--color-border-input);
    flex-shrink: 0;
    align-self: stretch;
    background: var(--color-bg-input);
  }

  &__trigger,
  &__cancel {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 100%;
    min-height: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    color: c.$enedis-neutral-400;
    outline: none;
  }
  &__trigger { border-radius: 0 3px 3px 0; }
  &__trigger .source-select__chevron,
  &__cancel .material-symbols-rounded {
    font-size: 24px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s;
  }
  &.is-open &__trigger .source-select__chevron { transform: rotate(180deg); }
  &__cancel { opacity: 0.8; }

  /*
   * max-height / overflow du panneau et du viewport : voir le bloc <style> non-scoped ci-dessous.
   * Le scoped ne cible pas le DOM rendu par Reka (pas de data-v sur le nœud .source-select__content).
   */
  &__content {
    z-index: 10001;
    min-width: 224px;
    border: 1px solid var(--color-border);
    border-radius: $spacing-2xs;
    box-shadow: var(--color-shadow-menu) !important;
    filter: none;
    background: var(--color-bg-input);
    opacity: 1;
    isolation: isolate;
  }
  &__content :deep(div) { background: var(--color-bg-input); opacity: 1; }

  &__viewport {
    padding: 0;
    background: var(--color-bg-input);
    opacity: 1;
    width: 100%;
    min-width: 0;
  }

  &__item {
    position: relative;
    display: flex;
    align-items: center;
    gap: $spacing-2xs;
    width: 100%;
    min-width: 0;
    padding: $spacing-2xs $spacing-xs $spacing-2xs $spacing-2xl;
    margin: 0;
    border: none;
    border-radius: 0;
    cursor: pointer;
    outline: none;
    color: c.$enedis-neutral-500;
    background: var(--color-bg-input);
    box-sizing: border-box;
    text-align: left;
    &:first-child { border-radius: $spacing-3xs $spacing-3xs 0 0; }
    &:last-child { border-radius: 0 0 $spacing-3xs $spacing-3xs; }
    &[data-highlighted],
    &[data-state="checked"] {
      background-color: var(--color-bg-hover-primary);
      color: var(--color-primary-hover);
    }
  }

  &__item-indicator {
    position: absolute;
    left: $spacing-2xs;
    display: flex;
    align-items: center;
    color: var(--color-primary-hover);
  }

  &.xsmall { @include source-select-size('text-2xs', 'text-2xs', 28px, 26px, 0 $spacing-2xs 0 $spacing-2xs); }
  &.small { @include source-select-size('text-2xs', 'text-xs', 32px, 30px, 0 $spacing-2xs 0 $spacing-xs); }
  &.medium { @include source-select-size('text-xs', 'text-s', 36px, 34px, 0 $spacing-2xs 0 $spacing-xs); }
}
</style>

<style lang="scss">
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/spacing" as *;

/* Non-scoped : le contenu du combobox est rendu par Reka sans le data-v du parent → le scoped ne s’appliquait pas. */
.source-select [data-reka-popper-content-wrapper]:has(.source-select__content) {
  max-height: 200px !important;
  overflow: hidden !important;
}

.source-select .source-select__content {
  box-shadow: var(--color-shadow-menu) !important;
  filter: none;
  max-height: 200px !important;
  min-height: 0 !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

/*
 * Liste à hauteur limitée + scroll vertical (scroll vs auto pour la piste).
 * Reka UI injecte un <style> global qui masque les barres ; !important pour les rétablir.
 */
.source-select [data-reka-combobox-viewport] {
  box-sizing: border-box;
  max-height: 184px !important;
  min-height: 0 !important;
  overflow-y: scroll !important;
  overflow-x: hidden !important;
  overscroll-behavior: contain;
  touch-action: pan-y;
  scrollbar-width: thin !important;
  -ms-overflow-style: auto !important;
}

.source-select [data-reka-combobox-viewport]::-webkit-scrollbar {
  display: block !important;
  width: $spacing-2xs;
}

.source-select [data-reka-combobox-viewport]::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: c.$enedis-neutral-100;
  border: 2px solid var(--color-bg-input, #fff);
}

.source-select [data-reka-combobox-viewport]::-webkit-scrollbar-track {
  background: var(--color-bg-input, #fff);
}
</style>
